/*eslint no-debugger: 0*/

import { AxiosError } from 'axios';
import { FieldError } from 'react-hook-form';

import { Nullable } from 'ts';
import { t } from 'locales/i18n';

const prettifyKey = (key: string) => {
  return key
    .split('_')
    .map(word => `${word[0].toUpperCase()}${word.substring(1)}`)
    .join(' ');
};

const handleValidationErrors = (data: Record<string, Array<string>>) => {
  return Object.keys(data).reduce((acc, key) => {
    const errors = (data as Record<string, Array<string>>)[key];

    if (key && Array.isArray(errors)) {
      return `${acc} ${prettifyKey(key)}: ${errors.join(', ')}`;
    }
    return acc;
  }, '');
};

export const convertError = (error_: unknown) => {
  const error = error_ as Nullable<AxiosError>;

  if ((error?.response?.status as number) >= 500) return t('errors.unexpectedServerError');

  const data = error?.response?.data;
  if (data?.non_field_errors) return data?.non_field_errors?.join(' ');

  return data?.detail || data || error?.message || t('errors.unexpectedServerError');
};

export const accumulateErrorMessage = (error_: unknown) => {
  const error = error_ as Nullable<AxiosError>;

  if ((error?.response?.status as number) >= 500) return t('errors.unexpectedServerError');

  const data = error?.response?.data;

  if (data?.non_field_errors) return data?.non_field_errors?.join(' ');

  const errorDescription = data?.detail;

  if (errorDescription) return errorDescription;

  if (Array.isArray(data)) {
    return data.reduce((acc, item) => {
      return acc + handleValidationErrors(item);
    }, '');
  } else if (Object.keys(data).length !== 0) {
    return handleValidationErrors(data);
  } else t('errors.unexpectedServerError');
};

type ParseFieldErrorsResult<T extends Record<keyof T, unknown>> = Array<FieldError & { field: keyof T }>;

export function parseFieldErrors<T extends Record<keyof T, unknown>>(
  errorResponse: Record<keyof T, []>
): ParseFieldErrorsResult<T> {
  if (typeof errorResponse === 'object') {
    return Object.keys(errorResponse).reduce(
      (acc, field): ParseFieldErrorsResult<T> => [
        ...acc,
        { message: errorResponse[field as keyof T]?.join?.(', '), field: field as keyof T, type: 'manual' },
      ],
      [] as ParseFieldErrorsResult<T>
    );
  }
  return [];
}
